import { Box, IconButton, Stack, useTheme } from "@mui/material";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoalActionBlockedDrawer } from "web_myplann/Goals/Components/GoalActionBlockedDrawer";
import { GoalCreatedPinDrawer } from "web_myplann/Goals/Components/GoalCreatedPinDrawer";
import { GoalDetailComposition } from "web_myplann/Goals/Components/GoalDetailComposition";
import { GoalDetailHero } from "web_myplann/Goals/Components/GoalDetailHero";
import { GoalDetailLoanCalculator } from "web_myplann/Goals/Components/GoalDetailLoanCalculator";
import { GoalDetailRentPlan } from "web_myplann/Goals/Components/GoalDetailRentPlan";
import { GoalDetailWhatIf } from "web_myplann/Goals/Components/GoalDetailWhatIf";
import {
	GoalPinningSection,
	type WelcomePinTrigger,
} from "web_myplann/Goals/Components/GoalPinningSection";
import { useGoalDetail } from "web_myplann/Goals/Hooks/useGoalDetail";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

type WelcomePinLocationState = {
	welcomePin?: boolean;
	ownFunds?: number;
};

export const GoalDetailPage = () => {
	const theme = useTheme();
	const { t } = useResource();
	const { id = "" } = useParams<{ id: string }>();
	const location = useLocation();
	const navigate = useNavigate();

	const { goal, dto: goalDto } = useGoalDetail(id);
	const [blockedOpen, setBlockedOpen] = useState(false);
	const [welcomePinOpen, setWelcomePinOpen] = useState(false);
	const [welcomePinOwnFunds, setWelcomePinOwnFunds] = useState(0);
	const [welcomePinTrigger, setWelcomePinTrigger] =
		useState<WelcomePinTrigger | null>(null);

	useEffect(() => {
		const state = location.state as WelcomePinLocationState | null;
		if (state?.welcomePin) {
			setWelcomePinOwnFunds(state.ownFunds ?? 0);
			setWelcomePinOpen(true);
			navigate(location.pathname, { replace: true, state: null });
		}
	}, [location.state, location.pathname, navigate]);

	const handleWelcomePinAssign = () => {
		setWelcomePinOpen(false);
		setWelcomePinTrigger({ hasOwnFunds: welcomePinOwnFunds > 0 });
	};

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Goals.Detail.title)}
					bgColor={theme.palette.background.paper}
					defaultRoute={Routing.Goals.GoalsList.path()}
					endComponent={
						goal?.completed && (
							<IconButton variant="soft" onClick={() => setBlockedOpen(true)}>
								<Pencil size={16} />
							</IconButton>
						)
					}
				/>
			}
		>
			{goal && (
				<Stack>
					<GoalDetailHero goal={goal} />
					<GoalPinningSection
						goal={goal}
						goalDto={goalDto}
						welcomePinTrigger={welcomePinTrigger}
						onWelcomePinTriggerHandled={() => setWelcomePinTrigger(null)}
					/>
					<Box mt={1}>
						<GoalDetailLoanCalculator goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailRentPlan goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailWhatIf goal={goal} />
					</Box>
					<Box mt={1}>
						<GoalDetailComposition goal={goal} />
					</Box>
				</Stack>
			)}
			<GoalActionBlockedDrawer
				open={blockedOpen}
				onClose={() => setBlockedOpen(false)}
				onMessageAdvisor={() => setBlockedOpen(false)}
			/>
			<GoalCreatedPinDrawer
				open={welcomePinOpen}
				ownFunds={welcomePinOwnFunds}
				onClose={() => setWelcomePinOpen(false)}
				onAssign={handleWelcomePinAssign}
			/>
		</MyPlannLayout>
	);
};
